'use strict';

import { BaseElement } from './BaseElement';

import intlTelInput from 'intl-tel-input';
import { UserPosition } from '../components/location/Location';
import { Cookie } from '../Cookie/Cookie';
import { changeLangToCurrent } from '../components/langSwitcher/langSwitcher';
import { PopUp } from './pages/PopUp';

const DEFAULT_PARAMS = {
	className: 'default-form-class',
	allowedKey: 'allowedText',
	titleKey: 'signInTitle',
	submitKey: 'singInSubmit',
	formName: 'defaultForm',
	countries: ['us','gb','ca','de','fr','il','es'],
	initialCountry: 'us',
	lang: null,
};

export class Form {
	/**
	 * @param {Object[]} fields
	 * @param {Object} params
	 * @param {string} [params.className=default-form-class]
	 * @param {string} [params.allowedKey=allowedText]
	 * @param {string} [params.titleKey=signInTitle]
	 * @param {string} [params.submitKey=singInSubmit]
	 * @param {string} [params.formName=defaultForm]
	 * @param {Object[]} [params.countries=[us,gb,ca,de,fr,il,es]]
	 * @param {string} [params.initialCountry=us]
	 * @param {string|null} [params.lang=null]
	 */
	
	constructor (fields,params) {
		this.params = Object.assign({},DEFAULT_PARAMS,params);
		
		const location = UserPosition.location.short;
		this.params.initialCountry = this.params.countries.includes(location) ? location : 'us';
		this.fields = fields;
		this.parent = null;
		this.submitBtn = null;
		this.onValidCallback = (formData) => {};
	}
	
	setOnValidCallback(func) {
		this.onValidCallback = func;
	}
	
	setCallbackToElement(name,event,func) {
		const element = this.getField(name);
		if (element) {
			element.addEventListener(event,func);
		}
	}
	
	create(parent) {
		if (parent) {
			this.parent = parent;
		} else {
			this.popUp = new PopUp({
				id: this.params.formName + '_popup',
				needsCloseBtn: true,
				destroy: true
			});
			this.popUp.onClose = () => {
				this.popUp.hide();
			};
			this.parent = this.popUp.content;
		}
		
		this.container = new BaseElement()
			.addClass('form',this.params.className +'-container')
			.appendTo(this.parent)
			.DOMElement;
		
		if (this.params.titleKey) {
			const header = new BaseElement()
				.addClass('form__header',this.params.className + '__header')
				.appendTo(this.container)
				.DOMElement;
			
			new BaseElement('h3')
				.addClass('form__title',this.params.className + '__title','language-string')
				.addAttribute('data-dictionary',this.params.titleKey)
				.innerHtml(changeLangToCurrent(this.params.titleKey))
				.appendTo(header);
		}
		
		this.form = new BaseElement('form')
			.addClass('form__form-element',this.params.className + '__form-element')
			.addAttribute('name',this.params.formName)
			.appendTo(this.container)
			.DOMElement;
		
		const userData = Cookie.userData();
		
		this.fields.forEach(field => {
			const input = new BaseElement('input')
				.addClass('form__input',this.params.className + '__input')
				.event('input',() => input.removeClass('sign-in__error'))
				.addAttribute('type',field.type)
				.addAttribute('value',field.value)
				.addAttribute('name',field.name)
				.appendTo(this.form);
			
			if (userData && userData[field.name]) {
				if (field.hasOwnProperty('userData')) {
					if (field.userData) {
						input.addAttribute('value',userData[field.name]);
					}
				} else {
					input.addAttribute('value',userData[field.name]);
				}
			}
			
			if (field.type === 'tel') {
				this.iti = intlTelInput(input.DOMElement, {
					containerClass: `form__iti`,
					utilsScript: '/js/libs/IntlTelUtils.js',
					formatOnDisplay: true,
					onlyCountries: this.params.countries,
					initialCountry: this.params.initialCountry,
					// customPlaceholder:  function() {
					// 	return Dictionary.getCurrentText('Enter a phone number');
					// }
				});
				
			} else if(field.placeholder) {
				input.addAttribute('placeholder',changeLangToCurrent(field.placeholder));
			}
			
			if (field.pattern) {
				input.addAttribute('pattern',field.pattern);
			}
			
			if (field.required) {
				input.addAttribute('required',true);
			}
			
			if (field.readonly) {
				input.addAttribute('readonly',true);
			}
			
			if (field.hasOwnProperty('userData')) {
				input.addAttribute('data-user-data',field.userData);
			}
			
		});
		
		if (this.params.allowedKey) {
			const allowedContainer = new BaseElement()
				.addClass('form__allowed-container',this.params.className + '__allowed-container')
				.appendTo(this.container)
				.DOMElement;
			
			new BaseElement('input')
				.addClass('form__allowed-input',this.params.className + '__allowed-input')
				.addAttribute('type','checkbox')
				.addAttribute('checked',true)
				.event('change', (e) => {
					if (e.target.checked) {
						this.submitBtn?.deleteAttribute('disabled');
					} else {
						this.submitBtn?.addAttribute('disabled',true);
					}
				})
				.appendTo(allowedContainer);
			
			new BaseElement('p')
				.addClass('form__allowed-text',this.params.className + '__allowed-text','language-string')
				.addAttribute('data-dictionary',this.params.allowedKey)
				.innerHtml(changeLangToCurrent(this.params.allowedKey))
				.appendTo(allowedContainer);
		}
		
		if (this.params.submitKey) {
			this.submitBtn = new BaseElement('button')
				.addClass('form__submit',this.params.className + '__submit','language-string')
				.addAttribute('data-dictionary',this.params.submitKey)
				.innerHtml(changeLangToCurrent(this.params.submitKey))
				.addAttribute('type','submit')
				.appendTo(this.container)
				.event('click', this.submit.bind(this));
		}
		
		this.show();
	}
	
	submit() {
		const elements = this.form.elements;
		const sendData = {};
		let valid = true;
		
		for (let i = 0; i < elements.length; i++) {
			const element = elements[i];
			if (element.required) {
				if (element.checkValidity()) {
					sendData[element.getAttribute('name')] = element.value;
				} else {
					element.classList.add('sign-in__error');
					valid = false;
				}
			}
		}
		
		if (this.iti) {
			const phoneNumber = this.iti.getNumber();
			if (phoneNumber) {
				if (this.iti.isValidNumber()) {
					sendData[this.iti.telInput.getAttribute('name')] = phoneNumber;
				} else {
					this.iti.telInput.classList.add('sign-in__error');
					valid = false;
				}
			}
		}
		
		if (valid) {
			const formData = new FormData();
			const countryId = UserPosition.location.countryId;
			if (countryId) {
				sendData.CountryID = countryId;
			}
			Object.entries(sendData).forEach(([key,value]) => {
				formData.append(key,value);
			});
			
			for (let i = 0; i < elements.length; i++) {
				const element = elements[i];
				if (!element.getAttribute('readonly')) {
					element.value = '';
				}
			}
			
			this.onValidCallback(formData);
		}
	}
	
	getField(field) {
		return this.form.elements[field];
	}
	
	show() {
		if (this.popUp) {
			this.popUp.show();
		}
	}
	
	setSubmitBtn(btn) {
		this.submitBtn = btn;
	}
	
	hide() {
		if (this.popUp) {
			this.popUp.hide();
		}
	}
	
	updateUserData() {
		const userData = Cookie.userData();
		const elements = this.form.elements;
		if (userData) {
			for (let i = 0; i < elements.length; i++) {
				if (userData[elements[i].name]) {
					const saveInUserData = elements[i].getAttribute('data-user-data');
					if (saveInUserData !== null) {
						if (saveInUserData) {
							elements[i].value = userData[elements[i].name];
						}
					} else {
						elements[i].value = userData[elements[i].name];
					}
				}
			}
		}
	}
	
	remove() {
		this.container.remove();
		this.container = null;
	}
	
}
